/**
 * MedicalHistoryPage Component
 *
 * Displays all doctor visit entries chronologically (most recent first)
 * with date range filtering and linked prescriptions.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../lib/api';
import { getLocalDateString } from '../lib/dateUtils';
import { useImages } from '../hooks/useImages';
import { ImageGrid } from '../components/ui/ImageThumbnail';
import { ImageLightbox } from '../components/ui/ImageLightbox';
import { useAuth } from '../hooks/useAuth';

interface Prescription {
  id: string;
  userId: string;
  visitId: string | null;
  date: Date;
  medicationName: string;
  dosage: string | null;
  frequency: string | null;
  duration: string | null;
  notes: string | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string | null;
}

interface MedicalVisit {
  id: string;
  userId: string;
  date: Date;
  doctorName: string;
  specialty: string | null;
  hospitalClinic: string | null;
  diagnosis: string | null;
  notes: string | null;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string | null;
  prescriptions?: Prescription[];
}

/**
 * Format date in Indonesian locale
 */
function formatDateIndonesian(date: Date): string {
  return new Date(date).toLocaleDateString('id-ID', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * VisitCard Component - displays a single medical visit with prescriptions
 */
function VisitCard({
  visit,
  canEdit,
  onEdit,
}: {
  visit: MedicalVisit;
  canEdit: boolean;
  onEdit: (visit: MedicalVisit) => void;
}) {
  const { images } = useImages('visit', visit.id);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      {/* Header with Date and Edit Button */}
      <div className="flex justify-between items-start mb-2">
        <div className="text-sm text-gray-500">
          {formatDateIndonesian(visit.date)}
        </div>
        {canEdit && (
          <button
            onClick={() => onEdit(visit)}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
            title="Edit kunjungan"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
        )}
      </div>

      {/* Doctor Name */}
      <p className="text-gray-900 font-medium">{visit.doctorName}</p>

      {/* Specialty */}
      {visit.specialty && (
        <p className="text-sm text-gray-600">{visit.specialty}</p>
      )}

      {/* Hospital/Clinic */}
      {visit.hospitalClinic && (
        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          {visit.hospitalClinic}
        </p>
      )}

      {/* Diagnosis */}
      {visit.diagnosis && (
        <p className="text-sm text-gray-700 mt-2">
          <span className="font-medium">Diagnosis:</span> {visit.diagnosis}
        </p>
      )}

      {/* Notes */}
      {visit.notes && (
        <p className="text-sm text-gray-500 mt-1 italic">{visit.notes}</p>
      )}

      {/* Prescriptions */}
      {visit.prescriptions && visit.prescriptions.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-sm font-medium text-gray-700 mb-2">Resep:</p>
          <div className="space-y-2">
            {visit.prescriptions.map((prescription) => (
              <div
                key={prescription.id}
                className="bg-gray-50 rounded-md p-2 text-sm"
              >
                <p className="font-medium text-gray-900">
                  {prescription.medicationName}
                </p>
                <div className="text-gray-600 mt-0.5 space-y-0.5">
                  {prescription.dosage && <p>Dosis: {prescription.dosage}</p>}
                  {prescription.frequency && (
                    <p>Frekuensi: {prescription.frequency}</p>
                  )}
                  {prescription.duration && (
                    <p>Durasi: {prescription.duration}</p>
                  )}
                </div>
                {prescription.notes && (
                  <p className="text-gray-500 mt-1 italic">{prescription.notes}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Images */}
      {images.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <ImageGrid
            images={images}
            columns={3}
            size="sm"
            onImageClick={(index) => setLightboxIndex(index)}
          />
        </div>
      )}

      {/* Image Lightbox */}
      <ImageLightbox
        images={images}
        initialIndex={lightboxIndex ?? 0}
        isOpen={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
      />
    </div>
  );
}

/**
 * EditVisitModal Component - modal for editing a medical visit
 */
function EditVisitModal({
  visit,
  isOpen,
  onClose,
  onSave,
}: {
  visit: MedicalVisit | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (visit: MedicalVisit) => void;
}) {
  const [formData, setFormData] = useState({
    date: '',
    doctorName: '',
    specialty: '',
    hospitalClinic: '',
    diagnosis: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when visit changes
  useEffect(() => {
    if (visit) {
      const visitDate = new Date(visit.date);
      setFormData({
        date: getLocalDateString(visitDate),
        doctorName: visit.doctorName || '',
        specialty: visit.specialty || '',
        hospitalClinic: visit.hospitalClinic || '',
        diagnosis: visit.diagnosis || '',
        notes: visit.notes || '',
      });
      setError(null);
    }
  }, [visit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!visit) return;

    setSaving(true);
    setError(null);

    try {
      const res = await fetch(apiUrl(`/api/medical/visits/${visit.id}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update visit');
      }

      const updatedVisit = await res.json();
      onSave({ ...visit, ...updatedVisit });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Gagal menyimpan perubahan');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !visit) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Edit Kunjungan</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Date */}
          <div>
            <label htmlFor="edit-date" className="block text-sm font-medium text-gray-700 mb-1">
              Tanggal
            </label>
            <input
              type="date"
              id="edit-date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Doctor Name */}
          <div>
            <label htmlFor="edit-doctorName" className="block text-sm font-medium text-gray-700 mb-1">
              Nama Dokter
            </label>
            <input
              type="text"
              id="edit-doctorName"
              value={formData.doctorName}
              onChange={(e) => setFormData({ ...formData, doctorName: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Specialty */}
          <div>
            <label htmlFor="edit-specialty" className="block text-sm font-medium text-gray-700 mb-1">
              Spesialisasi
            </label>
            <input
              type="text"
              id="edit-specialty"
              value={formData.specialty}
              onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Hospital/Clinic */}
          <div>
            <label htmlFor="edit-hospitalClinic" className="block text-sm font-medium text-gray-700 mb-1">
              Rumah Sakit / Klinik
            </label>
            <input
              type="text"
              id="edit-hospitalClinic"
              value={formData.hospitalClinic}
              onChange={(e) => setFormData({ ...formData, hospitalClinic: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Diagnosis */}
          <div>
            <label htmlFor="edit-diagnosis" className="block text-sm font-medium text-gray-700 mb-1">
              Diagnosis
            </label>
            <textarea
              id="edit-diagnosis"
              value={formData.diagnosis}
              onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="edit-notes" className="block text-sm font-medium text-gray-700 mb-1">
              Catatan
            </label>
            <textarea
              id="edit-notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 px-4 py-2 text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {saving ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function MedicalHistoryPage() {
  const { user } = useAuth();
  const [visits, setVisits] = useState<MedicalVisit[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterExpanded, setFilterExpanded] = useState(false);

  // Applied filter (only update on Apply button click)
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  // Pagination
  const [offset, setOffset] = useState(0);
  const limit = 20;

  // Edit modal state
  const [editingVisit, setEditingVisit] = useState<MedicalVisit | null>(null);

  // Only admin and superadmin can edit (NOT editor)
  const canEdit = user?.role === 'admin' || user?.role === 'superadmin';

  // Handle saving edited visit
  const handleSaveVisit = (updatedVisit: MedicalVisit) => {
    setVisits((prev) =>
      prev.map((v) => (v.id === updatedVisit.id ? updatedVisit : v))
    );
  };

  /**
   * Fetch visits from API
   */
  const fetchVisits = useCallback(
    async (resetOffset = false) => {
      setLoading(true);
      setError(null);

      const currentOffset = resetOffset ? 0 : offset;
      if (resetOffset) {
        setOffset(0);
      }

      try {
        // Build query params
        const params = new URLSearchParams();
        params.append('includePrescriptions', 'true');
        params.append('limit', limit.toString());
        params.append('offset', currentOffset.toString());

        if (appliedStartDate) {
          params.append('startDate', appliedStartDate);
        }
        if (appliedEndDate) {
          params.append('endDate', appliedEndDate);
        }

        const res = await fetch(apiUrl(`/api/medical/visits?${params.toString()}`), {
          credentials: 'include',
        });

        if (!res.ok) {
          throw new Error('Failed to fetch visits');
        }

        const data = await res.json();

        if (resetOffset) {
          setVisits(data.entries || []);
        } else {
          setVisits((prev) => [...prev, ...(data.entries || [])]);
        }
        setTotal(data.total || 0);
      } catch (err) {
        console.error('Failed to fetch medical visits:', err);
        setError('Gagal memuat data. Silakan coba lagi.');
      } finally {
        setLoading(false);
      }
    },
    [offset, appliedStartDate, appliedEndDate, limit]
  );

  // Initial fetch and when applied filter changes
  useEffect(() => {
    fetchVisits(true);
  }, [appliedStartDate, appliedEndDate]);

  /**
   * Apply date filter
   */
  const handleApplyFilter = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  /**
   * Reset date filter
   */
  const handleResetFilter = () => {
    setStartDate('');
    setEndDate('');
    setAppliedStartDate('');
    setAppliedEndDate('');
  };

  /**
   * Load more visits
   */
  const handleLoadMore = () => {
    setOffset((prev) => prev + limit);
  };

  // Effect to fetch when offset changes (for load more)
  useEffect(() => {
    if (offset > 0) {
      fetchVisits(false);
    }
  }, [offset]);

  const hasMore = visits.length < total;
  const hasActiveFilter = appliedStartDate || appliedEndDate;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Histori Medis</h1>
        <p className="text-gray-600 mt-1">
          Riwayat kunjungan dokter dan resep
        </p>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white shadow rounded-lg mb-4 overflow-hidden">
        {/* Filter Header - Clickable to expand/collapse */}
        <button
          type="button"
          onClick={() => setFilterExpanded(!filterExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition"
        >
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <span className="font-medium text-gray-700">Filter Tanggal</span>
            {hasActiveFilter && (
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                Aktif
              </span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${
              filterExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Filter Content */}
        {filterExpanded && (
          <div className="px-4 pb-4 pt-2 border-t border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {/* Start Date */}
              <div>
                <label
                  htmlFor="filter-start-date"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Dari
                </label>
                <input
                  type="date"
                  id="filter-start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11"
                />
              </div>

              {/* End Date */}
              <div>
                <label
                  htmlFor="filter-end-date"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Sampai
                </label>
                <input
                  type="date"
                  id="filter-end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-11"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleApplyFilter}
                className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition min-h-11"
              >
                Terapkan
              </button>
              <button
                type="button"
                onClick={handleResetFilter}
                className="px-4 py-2 text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition min-h-11"
              >
                Reset
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
          <button
            onClick={() => fetchVisits(true)}
            className="ml-2 text-red-800 underline hover:no-underline"
          >
            Coba lagi
          </button>
        </div>
      )}

      {/* Loading State - Initial Load */}
      {loading && visits.length === 0 && (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Empty State */}
      {!loading && visits.length === 0 && !error && (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <svg
            className="w-16 h-16 mx-auto text-gray-300 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-gray-500">Belum ada catatan kunjungan dokter</p>
        </div>
      )}

      {/* Visit List */}
      {visits.length > 0 && (
        <div className="space-y-4">
          {visits.map((visit) => (
            <VisitCard
              key={visit.id}
              visit={visit}
              canEdit={canEdit}
              onEdit={setEditingVisit}
            />
          ))}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && !loading && visits.length > 0 && (
        <div className="mt-6 text-center">
          <button
            onClick={handleLoadMore}
            className="px-6 py-2 text-blue-600 font-medium border border-blue-300 rounded-lg hover:bg-blue-50 transition min-h-11"
          >
            Muat lebih banyak
          </button>
        </div>
      )}

      {/* Loading More Indicator */}
      {loading && visits.length > 0 && (
        <div className="flex justify-center py-6">
          <div className="w-6 h-6 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Edit Visit Modal */}
      <EditVisitModal
        visit={editingVisit}
        isOpen={editingVisit !== null}
        onClose={() => setEditingVisit(null)}
        onSave={handleSaveVisit}
      />
    </div>
  );
}
